/**
 * Top-Y cap for an over-fridge cabinet — and for the fridge surround that
 * encloses it. Pure; shared by materializeCandidate (layout time) and the
 * style_kitchen GLB re-fit (functions layer).
 *
 * The cap is the MINIMUM of the same-wall regular uppers' top edge (when the
 * run has any) and the host wall's own top. It is NEVER a room-wide ceiling:
 * on a sloped-ceiling room (Talkeetna, structure 83E09CA9) the tallest wall
 * tops out at 1.896 while the fridge's run wall tops out at 1.147, and a
 * room-wide cap ran the cabinet 0.75 m through the roof plane.
 */

const OVER_FRIDGE_LABEL = 'Over-Fridge Cabinet';

function isUpperCabinet(o) {
  const t = (o?.type || o?.objectType || '').toLowerCase();
  return t === 'upper-cabinet' || Boolean(o?.category?.['upper-cabinet']);
}

/**
 * A corner unit (the L-shaped / blind corner piece), by type or category —
 * the same test `resolveCabinetRendererType` (web-app) makes before choosing
 * the corner renderer. A corner unit may also carry an upper / lower
 * category, so this is checked alongside, not instead of, `isUpperCabinet`.
 */
function isCornerCabinet(o) {
  const t = (o?.type || o?.objectType || '').toLowerCase();
  return t === 'corner-cabinet' || Boolean(o?.category?.['corner-cabinet']);
}

/**
 * The upper that hangs over a fridge. Every producer stamps the one label
 * (the run engine's fridge install, the scan re-seat, style_kitchen's re-fit
 * matches on it), so it is the one test.
 */
function isOverFridgeCabinet(o) {
  return o?.label === OVER_FRIDGE_LABEL;
}

/**
 * Regular run uppers only — the over-fridge cabinet itself is not a cap
 * source, and neither is a STACKED upper (`category.stack`, plan 2026-10-21):
 * it stands on the uppers' line, and a stacked run's column line is the
 * run's (`materializeRun.stackedColumnLineY`), not its boxes'.
 */
function isRegularUpperCabinet(o) {
  return isUpperCabinet(o) && !isOverFridgeCabinet(o) && !o?.category?.stack;
}

/**
 * Wall ids an object is attached to, across both layout schemas:
 * materializeCandidate objects carry `parentWallIds`; applyKitchenLayout /
 * fromPhaseB objects drop those for `cabinetWallRefs` (only `real` refs map
 * to room.walls ids).
 */
function objectWallIds(o) {
  const ids = [];
  if (Array.isArray(o?.parentWallIds)) {
    for (const id of o.parentWallIds) if (id) ids.push(id);
  }
  if (Array.isArray(o?.cabinetWallRefs)) {
    for (const r of o.cabinetWallRefs) {
      if (r && r.kind === 'real' && r.id) ids.push(r.id);
    }
  }
  return ids;
}

/** World-Y top edge of a wall, or null when it carries no vertical data. */
function wallTopY(wall) {
  const h = wall?.dimensions?.y ?? wall?.dimensions?.height;
  const cy = wall?.center?.y;
  if (typeof h !== 'number' || typeof cy !== 'number') return null;
  return cy + h / 2;
}

/**
 * @param {Object|null} wall - The fridge's host wall.
 * @param {Array} objects - Room/candidate objects (for the run's uppers).
 * @returns {number|null} The cabinet-top cap, or null when neither the run's
 *   uppers nor the wall's own top is derivable — the caller decides (skip the
 *   adjustment / the cabinet), never substituting a room-wide ceiling.
 */
function overFridgeCapY(wall, objects) {
  const tops = [];
  for (const o of objects ?? []) {
    if (!isRegularUpperCabinet(o)) continue;
    if (!wall?.id || !objectWallIds(o).includes(wall.id)) continue;
    const dy = o.dimensions?.y;
    if (typeof dy !== 'number' || typeof o.center?.y !== 'number') continue;
    tops.push(o.center.y + dy / 2);
  }
  const candidates = [];
  if (tops.length) candidates.push(Math.max(...tops)); // the run's top edge
  const wTop = wallTopY(wall);
  if (wTop != null) candidates.push(wTop);
  return candidates.length ? Math.min(...candidates) : null;
}

const NESTED_EPS = 1e-6;

/** World-Y top of an object, or null without center/dimensions data. */
function objectTopY(o) {
  const dy = o?.dimensions?.y;
  const cy = o?.center?.y;
  if (typeof dy !== 'number' || typeof cy !== 'number') return null;
  return cy + dy / 2;
}

/** Max top of the room's regular uppers (any wall), or null when none. */
function regularUppersTopLine(objects) {
  const tops = [];
  for (const o of objects ?? []) {
    if (!isRegularUpperCabinet(o)) continue;
    const top = objectTopY(o);
    if (top != null) tops.push(top);
  }
  return tops.length ? Math.max(...tops) : null;
}

/**
 * Cap for a specific over-fridge installation piece (the surround or the
 * cabinet itself). The wall-based `overFridgeCapY` wins when a room wall is
 * given — but a fridge column on a cabinet-run VIRTUAL wall (`cw_*` refs,
 * peninsula/island) resolves no room wall, so fall back to:
 *   (i)  the over-fridge cabinet sharing the piece's fixtureGroupId, when it
 *        is nested inside the piece's own vertical span (cap-down case —
 *        the surround shrinks onto the cabinet it encloses);
 *   (ii) the room's regular-uppers top line — "ending at the top edge of
 *        the other uppers";
 *   else null (the caller leaves the piece alone and warns).
 */
function overFridgeCapYFor(obj, wall, objects) {
  const wallCap = overFridgeCapY(wall, objects);
  if (wallCap != null) return wallCap;
  const objTop = objectTopY(obj);
  if (obj?.fixtureGroupId && objTop != null) {
    for (const o of objects ?? []) {
      if (o === obj || o?.fixtureGroupId !== obj.fixtureGroupId) continue;
      if (o.label !== OVER_FRIDGE_LABEL) continue;
      const top = objectTopY(o);
      // A group cabinet sitting ON TOP of the piece (post-processed shape)
      // is not a cap-down source — only a nested one is.
      if (top != null && top <= objTop + NESTED_EPS) return top;
    }
  }
  return regularUppersTopLine(objects);
}

module.exports = {
  overFridgeCapY,
  overFridgeCapYFor,
  objectWallIds,
  OVER_FRIDGE_LABEL,
  isOverFridgeCabinet,
  isUpperCabinet,
  isCornerCabinet,
};
